/**
 * ExifDisplay Component - Display EXIF metadata below histogram
 * Feature: 012-histogram-display
 * User Story 3: Reference EXIF Shooting Parameters
 */

import { ExifDisplayData } from '../../types/histogram';

export interface ExifDisplayProps {
  /**
   * Formatted EXIF data to display
   */
  exifData?: ExifDisplayData;

  /**
   * Additional CSS class names
   */
  className?: string;
}

/**
 * ExifDisplay - Shows camera shooting parameters (ISO, focal length, aperture, shutter speed)
 *
 * @example
 * <ExifDisplay exifData={{ iso: "ISO 1600", focalLength: "55mm", aperture: "f/2.8", shutterSpeed: "1/125" }} />
 */
export function ExifDisplay({
  exifData,
  className = '',
}: ExifDisplayProps): JSX.Element {
  // If no EXIF data, show informative message
  if (!exifData || Object.keys(exifData).filter((key) => exifData[key as keyof ExifDisplayData]).length === 0) {
    return (
      <div className={`text-xs text-muted text-center py-2 ${className}`}>
        No EXIF data available
      </div>
    );
  }

  // Filter out undefined values
  const availableData = Object.entries(exifData).filter(([_, value]) => value !== undefined);

  return (
    <div className={`flex justify-between text-xs text-muted px-2 py-2 border-t border-border ${className}`}>
      {availableData.map(([key, value]) => (
        <span key={key} className="whitespace-nowrap" title={key}>
          {value}
        </span>
      ))}
    </div>
  );
}
